import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/types";

interface AuthContextValue {
  session: Session | null;
  profile: Profile | null;
  roleName: string | null;
  permissions: string[];
  isAdmin: boolean;
  loading: boolean;
  hasPermission: (code: string) => boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithPin: (pin: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  reloadProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setAuthLoading(false);
      })
      .catch(() => {
        if (active) setAuthLoading(false);
      });

    // Callback stays synchronous: it only updates local auth state.
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data: prof, error } = await supabase
        .from("profiles")
        .select("id, full_name, last_name, role_id, branch_id, internal_email, phone, active")
        .eq("id", userId)
        .maybeSingle();

      if (error || !prof) {
        setProfile(null);
        setRoleName(null);
        setPermissions([]);
        return;
      }

      setProfile(prof as Profile);

      const [roleRes, permRes, roleLinkRes, userLinkRes] = await Promise.all([
        supabase.from("roles").select("name").eq("id", prof.role_id).maybeSingle(),
        supabase.from("permissions").select("id, code"),
        supabase.from("role_permissions").select("permission_id").eq("role_id", prof.role_id),
        supabase.from("user_permissions").select("permission_id").eq("profile_id", userId),
      ]);

      setRoleName((roleRes.data as { name: string } | null)?.name ?? null);

      const codeById = new Map<string, string>();
      (permRes.data ?? []).forEach((row: { id: string; code: string }) => {
        codeById.set(row.id, row.code);
      });

      const codes = new Set<string>();
      (roleLinkRes.data ?? []).forEach((row: { permission_id: string | null }) => {
        if (row.permission_id) {
          const code = codeById.get(row.permission_id);
          if (code) codes.add(code);
        }
      });
      (userLinkRes.data ?? []).forEach((row: { permission_id: string | null }) => {
        if (row.permission_id) {
          const code = codeById.get(row.permission_id);
          if (code) codes.add(code);
        }
      });

      setPermissions(Array.from(codes));
    } catch {
      setProfile(null);
      setRoleName(null);
      setPermissions([]);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setProfile(null);
      setRoleName(null);
      setPermissions([]);
      return;
    }
    loadProfile(userId);
  }, [session?.user?.id, loadProfile]);

  const isAdmin = roleName === "admin";

  const hasPermission = useCallback(
    (code: string) => isAdmin || permissions.includes(code),
    [isAdmin, permissions],
  );

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    return { error: error ? error.message : null };
  }, []);

  const signInWithPin = useCallback(async (pin: string) => {
    const { data, error } = await supabase.functions.invoke("rkc-pin-login", {
      body: { pin },
    });
    if (error) {
      return { error: "No se pudo verificar el PIN. Intenta de nuevo." };
    }
    const payload = data as { error?: string; token_hash?: string } | null;
    if (!payload || payload.error || !payload.token_hash) {
      return { error: payload?.error ?? "PIN incorrecto" };
    }
    const { error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: payload.token_hash,
      type: "magiclink",
    });
    return { error: verifyError ? verifyError.message : null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setRoleName(null);
    setPermissions([]);
  }, []);

  const reloadProfile = useCallback(async () => {
    if (session?.user?.id) await loadProfile(session.user.id);
  }, [session?.user?.id, loadProfile]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile,
      roleName,
      permissions,
      isAdmin,
      loading: authLoading || (!!session && profileLoading && !profile),
      hasPermission,
      signInWithPassword,
      signInWithPin,
      signOut,
      reloadProfile,
    }),
    [
      session,
      profile,
      roleName,
      permissions,
      isAdmin,
      authLoading,
      profileLoading,
      hasPermission,
      signInWithPassword,
      signInWithPin,
      signOut,
      reloadProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}